import { useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Home, LogOut, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LocaleToggle } from "@/components/shell/LocaleToggle";
import { signOut } from "@/lib/auth";
import { HelpCenter } from "@/features/help/HelpCenter";

// Phase 6.11.3 - Minimal tenant layout.
// Deliberately spare: no manager sidebar, org switcher, or admin nav. Just a
// header with the MapleKey mark and a sign-out, and the routed content.
// Mobile-first; 44px touch targets.
// C18: language toggle for tenants, limited to the fully translated locales.

const TENANT_LOCALES = ["en-CA", "fr-CA"] as const;

export function TenantLayout() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [helpOpen, setHelpOpen] = useState(false);
  const handleSignOut = async () => {
    await signOut();
    navigate("/login", { replace: true });
  };
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-card">
        <div className="mx-auto flex h-14 max-w-2xl items-center justify-between px-4">
          <div className="flex items-center gap-2 font-semibold">
            <Home className="h-5 w-5 text-primary" />
            <span>MapleKey</span>
          </div>
          <div className="flex items-center gap-1">
            <LocaleToggle locales={TENANT_LOCALES} />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setHelpOpen(true)}
              className="h-11 min-w-11"
              aria-label={t("nav.help", "Help")}
            >
              <HelpCircle className="h-4 w-4" />
              <span className="ml-1 hidden sm:inline">{t("nav.help", "Help")}</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSignOut}
              className="h-11 min-w-11"
              aria-label={t("auth.signOut", "Sign out")}
            >
              <LogOut className="h-4 w-4" />
              <span className="ml-1 hidden sm:inline">{t("auth.signOut", "Sign out")}</span>
            </Button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-2xl px-4 py-6">
        <Outlet />
      </main>
      <HelpCenter open={helpOpen} onOpenChange={setHelpOpen} persona="tenant" />
    </div>
  );
}
